import {
	addAppointment,
	AppointmentDayOfWeek,
	AppointmentDetailModel,
	AppointmentDetailViewModel,
	AppointmentRecurrencePatternKind,
	AppointmentRecurrenceRangeKind,
	AppointmentWeekIndex,
	AttendeeKind,
	AttendeeModel,
	CalendarColor,
	getAppointmentDetails,
	LocationModel,
	RecurrenceOptionsModel,
	RecurrencePatternModel,
	RecurringAppointmentOperationKind,
	updateCalendarAppointment
} from "client/Api/Calendar/calendar";
import {
	DetailsBoxAction,
	DetailsBox,
	DropOver,
	useConfirmationDialog,
	Form,
	FormItem,
	Select,
	ValidationError,
	TextInput,
	SelectItem,
	AlertBar,
	RichTextEditorContent,
	RichTextEditorIntermediateStateHandlers,
	useRichTextEditorIntermediateState,
	SuggestedMultiComboBoxItem
} from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { Coordinate } from "client/Misc/commonTypes";
import {
	isNil,
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import {
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	convertCalendarAppointmentFormModelToCreateModel,
	staticAppointmentValidation,
	isEndTimeValid,
	convertCalendarAppointmentFormModelToUpdateModel,
	operationKindToInfoMessageMap,
	calendarAppointmentFormDefaultData,
	getSelectedCalendarIdByDefault,
	AttendeesPathReplacementPattern
} from "../../ComposeAppointmentDialog/ComposeAppointmentDialog";
import styles from "./ComposeAppointmentBox.module.less";
import { AttendeesInput, DisplayContactModelWithoutId } from "../../AttendeesInput";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { SortDirection } from "client/Api/common";
import { DateSelect } from "../../DateSelect";
import { inline } from "client/Validation/rules";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import { PathRedirect } from "client/Validation/serverErrors";
import {
	addDays,
	addTime,
	getDayOfMonth
} from "client/DateAndTime/dateTimeUtils";
import { getHighlight } from "client/Misc/highlight";
import {
	RecurrencePatternKind,
	RecurrenceRangeFormModel,
	CalendarAppointmentFormModel,
	CalendarItem,
	CustomRecurrenceOptionsModel,
	CustomRecurrencePatternKind,
	WeeklyCustomRecurrenceOptionsModel,
	RecurrenceOptionsFormModel,
	AppointmentLocationFormData
} from "../../common";
import { never } from "client_legacy/ndaw/errors";
import {
	getAppointmentDayOfWeek,
	getRecurrencePatternKindSelectorOptions,
	getWeekIndexInMonth
} from "../../CalendarSidebarContent/RepeatOptionSelector/RecurrencePatternKindSelector";
import { LocationSelectItem } from "../../LocationSelectItem";
import { OnlineMeeting } from "client/Api/Notifications/notifications";
import { calendarColorToEventCardColorMap } from "../Header/Header";
import { EventCardColor } from "../EventCard/EventCard";
import { AppointmentSpanInfo, calendarLogic } from "client_legacy/Workspace/LiveInformation/Calendar/calendarLogic";

export type ComposeAppointmentBoxProps = {
	target: HTMLElement;
	coordinate: Coordinate;
	trackingId: string;
	onClose: (savedCalendarIds?: readonly string[]) => void;
	onEnlarge: (initialData: CalendarAppointmentFormModel) => void;
	calendars: readonly CalendarItem[];
	timezone: TimeZone;
	initialData: ComposeAppointmentBoxInitialData;
};

export const ComposeAppointmentBox = (props: ComposeAppointmentBoxProps): ReactElement => {
	const {
		target,
		coordinate,
		trackingId,
		onClose,
		onEnlarge,
		calendars,
		initialData,
		timezone
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [wasRecurrenceMessageDismissed, setWasRecurrenceMessageDismissed]
		= useState<boolean>(false);
	const [formData, setFormData] = useState<CalendarAppointmentFormModel>({
		...calendarAppointmentFormDefaultData,
		calendarId: getSelectedCalendarIdByDefault(calendars)
	});
	const richTextEditorState: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<CalendarAppointmentFormModel> => {

			let result: IValidationConfiguration<CalendarAppointmentFormModel> = staticAppointmentValidation;

			result = result.clone();

			result.when(
					x => !isEndTimeValid(
						x.startTime,
						x.endTime,
						x.isAllDay,
						timezone
					)
				)
				.forScalar("endTime")
				.add(
					inline((): InputValidationResult => {
						return {
							kind: InputValidationResultKind.Invalid,
							errorMessage: localize("_EndDateTimeMustBeGreaterThanStartDateTime")
						};
					})
				);

			return result;
		},
		[timezone]
	);

	const formHandler: IFormHandler<CalendarAppointmentFormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isFirstAttendeeAddedRef: RefObject<boolean> = useRef<boolean>(false);

	const recurringOperationKind: RecurringAppointmentOperationKind | undefined | null = formData
		?.recurringOperationKind;

	const isEdit: boolean = isNotNil(formData?.id);
	const meeting: OnlineMeeting | null = !isCreateAppointmentBoxInitialData(initialData)
		? initialData.meeting
		: null;

	const calendarsOptions = useMemo(
		(): readonly SelectItem<string>[] => {
			return sortBy(
				calendars,
				x => x.isDefault,
				SortDirection.Descending
			)
				.filter(x => x.canEdit)
				.map(
					(x: CalendarItem): SelectItem<string> => ({
						text: x.name,
						value: x.id
					})
				);
		},
		[calendars]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			let serverValidationPathRedirects: readonly PathRedirect[] | undefined = undefined;

			const validation: IRootValidation<CalendarAppointmentFormModel> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				if (!isNotNilOrEmpty(formData.title)) {
					const result: TryResult<void> = await openConfirmationDialog({
						title: localize("_AreYouSureYouWantToSaveThisAppointmentWithoutATitle"),
						confirmText: localize("_Save"),
						trackingId: "save-appointment-without-a-title-confirmation-dialog"
					});

					if (!result.wasSuccessful) {
						setIsBusy(false);
						return;
					}
				}

				if (isNotNil(initialData?.id)) {
					await updateCalendarAppointment(
						initialData.id,
						convertCalendarAppointmentFormModelToUpdateModel(
							formData,
							timezone,
							false
						),
						globalCancellation
					);
				} else {
					serverValidationPathRedirects = [
						{
							pattern: new DotPathWildcardPattern("ReminderMinutes.*?"),
							replacement: "ReminderMinutes"
						},
						{
							pattern: new DotPathWildcardPattern("IsPrivate.*?"),
							replacement: "IsPrivate"
						},
						{
							pattern: new AttendeesPathReplacementPattern(),
							replacement: "Attendees"
						}
					];

					await addAppointment(
						convertCalendarAppointmentFormModelToCreateModel(formData, timezone),
						globalCancellation
					);
				}

				alertManager.showSuccess(localize("_SuccessfullySavedTheAppointment"));

				let mutatedCalendarIds: (string[]) | undefined;

				if (isNotNil(formData.calendarId)) {
					if (
						!isCreateAppointmentBoxInitialData(initialData)
							&& initialData.calendar
								.id !== formData.calendarId
					) {
						mutatedCalendarIds = [
							initialData.calendar.id,
							formData.calendarId
						];
					} else {
						mutatedCalendarIds = [
							formData.calendarId
						];
					}
				}

				onClose(mutatedCalendarIds);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e, serverValidationPathRedirects)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			initialData,
			isBusy,
			onClose,
			openConfirmationDialog,
			timezone
		]
	);

	const enlargeHandler = useCallback(
		(): void => {
			onEnlarge(formData);
		},
		[
			formData,
			onEnlarge
		]
	);

	const firstAttendeeChangeHandler = useCallback(
		(
			attendees: readonly SuggestedMultiComboBoxItem<DisplayContactModelWithoutId>[]
		): void => {
			if (attendees.length > 0
				&& !isFirstAttendeeAddedRef.current
			) {
				isFirstAttendeeAddedRef.current = true;

				formHandler
					.for("isTeamsMeeting")
					.whenType(
						x => x.isSet,
						x => x.reducer
							.setValue({
								isSet: true,
								value: true
							})
					);
			}
		},
		[formHandler]
	);

	const actions = useMemo(
		(): readonly DetailsBoxAction[] => {
			return [
				{
					kind: "transparent-for-list",
					text: localize("_MoreOptions"),
					trackingId: "more-options-button",
					onClick: enlargeHandler
				},
				{
					kind: "primary",
					text: localize("_Save"),
					onClick: submitHandler,
					trackingId: "save-button"
				}
			];
		},
		[
			enlargeHandler,
			submitHandler
		]
	);

	useEffect(
		() => {
			const load = async (signal: AbortSignal): Promise<void> => {
				if (
					isCreateAppointmentBoxInitialData(initialData)
					|| initialData.recurringOperationKind === RecurringAppointmentOperationKind.Current
					|| isNil(initialData.masterId)
				) {
					setFormData(
						convertAppointmentBoxInitialDataToFormData(
							initialData,
							calendars,
							timezone
						)
					);

					return;
				}

				setIsBusy(true);

				try {
					const viewModel: AppointmentDetailViewModel = await getAppointmentDetails(
						{
							id: initialData.masterId,
							calendarId: initialData.calendar.id
						},
						signal
					);

					const appointment: AppointmentDetailModel = viewModel.model;

					setFormData(
						convertAppointmentBoxInitialDataToFormData(
							{
								...appointment,
								// Include the master appointment data in the form, but use the currently selected appointment ID.
								// This is necessary to identify the starting point for mutating the appointments when selecting "CurrentAndFollowing".
								// The start and end dates should also be the currently selected ones when RecurringAppointmentOperationKind === CurrentAndFollowing
								id: initialData.id,
								start: initialData.recurringOperationKind === RecurringAppointmentOperationKind.CurrentAndFollowing
									? initialData.start
									: appointment.start,
								end: initialData.recurringOperationKind === RecurringAppointmentOperationKind.CurrentAndFollowing
									? initialData.end
									: appointment.end,
								recurringOperationKind: initialData.recurringOperationKind
							},
							calendars,
							timezone
						)
					);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			calendars,
			initialData,
			onClose,
			timezone
		]
	);

	const detailsBoxColor = useMemo(
		(): EventCardColor => {
			const selectedCalendarId: string | null = formHandler.for("calendarId")
				.value;

			const selectedCalendar: CalendarItem | undefined = isNotNil(selectedCalendarId)
				? calendars.find((x) => x.id === selectedCalendarId)
				: undefined;

			const color: CalendarColor = selectedCalendar?.isEnabled
				? selectedCalendar.color
				: CalendarColor.Info;

			return calendarColorToEventCardColorMap[color];
		},
		[calendars, formHandler]
	);

	const isCalendarSelectDisabled = useMemo(
		(): boolean => {
			if (isCreateAppointmentBoxInitialData(initialData)) {
				return false;
			}

			const hasAttendees: boolean = initialData.attendees
				.length > 0;

			const isPartialRecurrence: boolean = isNotNil(recurringOperationKind)
				&& recurringOperationKind !== RecurringAppointmentOperationKind.All;

			return isPartialRecurrence || hasAttendees;
		},
		[initialData, recurringOperationKind]
	);

	return (
		<>
			<DropOver
				onClose={!confirmationDialog
					? onClose
					: undefined
				}
				closeOnMouseDown
				target={target}
				targetAlignment={{
					kind: "absolute",
					coordinate
				}}
			>
				<DetailsBox
					trackingId={trackingId}
					title={localize(
						isEdit
							? "_EditEvent"
							: "_CreateEvent"
					)}
					onClose={onClose}
					onEnlarge={enlargeHandler}
					hasBusyIndicator={isBusy}
					width="auto"
					variant={detailsBoxColor}
					actions={actions}
				>
					{!wasRecurrenceMessageDismissed
						&& isEdit
						&& isNotNil(recurringOperationKind)
						&& (
							<div className={styles.inlineAlertContainer}>
								<AlertBar
									kind="info"
									isInline
									onDismiss={() => setWasRecurrenceMessageDismissed(true)}
								>
									{localize(
										operationKindToInfoMessageMap[recurringOperationKind]
									)}
								</AlertBar>
							</div>
						)
					}
					<div className={styles.content}>
						<Form
							height="fill"
							onSubmit={submitHandler}
						>
							<DetailsBox.List>
								<DetailsBox.Item
									icon="calendar"
									variant="input-container"
								>
									<FormItem
										hasNoMargin
									>
										<Select
											{...formHandler.for("calendarId").attachToSelect()}
											options={calendarsOptions}
											menuTitle={localize("_Calendar")}
											isDisabled={isCalendarSelectDisabled}
										/>
										<ValidationError {...formHandler.for("calendarId").attachToValidationError()} />
									</FormItem>
								</DetailsBox.Item>

								<DetailsBox.Item
									icon="text"
									variant="input-container"
								>
									<FormItem
										hasNoMargin
									>
										<TextInput
											{...formHandler.for("title").attachToTextInput()}
											placeholder={localize("_EnterTitle")}
											autoFocus="focus"
										/>
										<ValidationError {...formHandler.for("title").attachToValidationError()} />
									</FormItem>
								</DetailsBox.Item>

								<DetailsBox.Item
									icon="user-group"
									variant="input-container"
								>
									<FormItem
										hasNoMargin
									>
										<AttendeesInput
											attachable={formHandler.for("attendees")}
											onAfterAttendeeChange={firstAttendeeChangeHandler}
										/>
										<ValidationError {...formHandler.for("attendees").attachToValidationError()} />
									</FormItem>
								</DetailsBox.Item>

								<DetailsBox.Item
									icon="clock"
									variant="input-container"
								>
									<DateSelect
										attachable={formHandler}
										isMobile={isMobile}
										timezone={timezone}
									/>
								</DetailsBox.Item>

								<DetailsBox.Item
									icon="location-dot"
									variant="input-container"
								>
									<LocationSelectItem
										attachable={formHandler}
										meeting={meeting}
										timezone={timezone}
									/>
								</DetailsBox.Item>

								<DetailsBox.Item
									icon="bars-sort"
									variant="input-container"
								>
									<FormItem
										hasNoMargin
									>
										<RichTextEditorContent
											{...formHandler.for("description").attachToRichTextEditorContent(richTextEditorState)}
											placeholder={localize("_EnterDescription")}
											height={70}
										/>
										<ValidationError {...formHandler.for("description").attachToValidationError()} />
									</FormItem>
								</DetailsBox.Item>
							</DetailsBox.List>
						</Form>
					</div>
				</DetailsBox>
			</DropOver>
			{confirmationDialog}
		</>
	);
};
ComposeAppointmentBox.displayName="ComposeAppointmentBox";

type CreateAppointmentBoxInitialData = {
	id?: undefined;
	startTime: Date;
	isAllDay: boolean;
};

type UpdateAppointmentBoxInitialData = AppointmentDetailModel & {
	recurringOperationKind: RecurringAppointmentOperationKind | undefined;
};

export type ComposeAppointmentBoxInitialData = CreateAppointmentBoxInitialData | UpdateAppointmentBoxInitialData;

function isCreateAppointmentBoxInitialData(
	model: ComposeAppointmentBoxInitialData
): model is CreateAppointmentBoxInitialData {
	return isNil(model.id);
}

function convertAppointmentBoxInitialDataToFormData(
	model: ComposeAppointmentBoxInitialData,
	calendars: readonly CalendarItem[],
	timezone: TimeZone
): CalendarAppointmentFormModel {
	if (isCreateAppointmentBoxInitialData(model)) {
		const {
			isAllDay,
			startTime,
		} = model;

		return {
			...calendarAppointmentFormDefaultData,
			calendarId: getSelectedCalendarIdByDefault(calendars),
			isAllDay,
			startTime,
			endTime: isAllDay
				? startTime
				: addTime(
					startTime,
					30,
					"minute"
				)
		};
	}

	return convertAppointmentDetailsToFormData(
		model,
		model.recurringOperationKind,
		calendars,
		timezone
	);
}

export function convertAppointmentDetailsToFormData(
	model: AppointmentDetailModel,
	recurringOperationKind: RecurringAppointmentOperationKind | undefined,
	calendars: readonly CalendarItem[],
	timezone: TimeZone
): CalendarAppointmentFormModel {
	const calendar: CalendarItem | undefined = calendars.find(
		x => x.id === model.calendar
			.id
	);

	if (isNil(calendar)) {
		throw new Error("calendar not found");
	}

	const spanInfo: AppointmentSpanInfo = calendarLogic.getAppointmentSpanInfo(model, timezone);
	const startTime: Date = spanInfo.startDateToCompare;

	const endTime: Date = model.isAllDayEvent
		? addDays(
			spanInfo.endDateToCompare,
			-1,
			timezone
		)
		: spanInfo.endDateToCompare;

	return {
		id: model.id,
		startTime,
		endTime,
		isAllDay: model.isAllDayEvent,
		description: model.description ?? "",
		title: model.subject ?? "",
		calendarId: calendar.id,
		isPrivate: {
			isSet: true,
			value: model.isPrivate
		},
		reminderMinutes: {
			isSet: true,
			value: model.reminderBeforeInMinutes
		},
		attendees: model.attendees
			.filter((x: AttendeeModel): boolean => x.kind !== AttendeeKind.Resource)
			.map((x: AttendeeModel): SuggestedMultiComboBoxItem<DisplayContactModelWithoutId> => {
				return {
					text: x.displayName,
					value: {
						displayName: x.displayName,
						emailAddress: x.emailAddress
					},
					highlight: getHighlight(x.displayName, ""),
					valueTrackingId: x.emailAddress
				};
			}),
		locations: model.locations
			.map(
				(x: LocationModel): SuggestedMultiComboBoxItem<AppointmentLocationFormData> => {
					return {
						text: x.displayName,
						value: isNotNil(x.id)
							? {
								id: x.id,
								displayName: x.displayName,
								availabilityKind: null
							}
							: {
								displayName: x.displayName
							},
						highlight: getHighlight(x.displayName, ""),
						color: "secondary"
					};
				}
			),
		recurringOperationKind,
		isTeamsMeeting: isNotNil(model.meeting)
			? { isSet: false}
			: {
				isSet: true,
				value: false
			},
		recurrenceOptions: convertRecurrenceOptionsModelToFormModel(
			model.recurrenceOptions,
			new Date(model.start),
			timezone
		)
	};
}

export function convertRecurrenceOptionsModelToFormModel(
	model: RecurrenceOptionsModel | null,
	startDate: Date,
	timezone: TimeZone
): RecurrenceOptionsFormModel {
	const {
		recurrenceOptions: defaultRecurrenceOptions
	} = calendarAppointmentFormDefaultData;
	const appointmentDayOfWeek: AppointmentDayOfWeek = getAppointmentDayOfWeek(
		startDate,
		timezone
	);

	const defaultCustomRecurrenceOptions: CustomRecurrenceOptionsModel = {
		...defaultRecurrenceOptions.customRecurrenceOptions,
		weeklyCustomRecurrenceOptions: {
			daysOfWeek: [appointmentDayOfWeek]
		}
	};

	if (!model) {
		return {
			recurrencePatternKind: defaultRecurrenceOptions.recurrencePatternKind,
			customRecurrenceOptions: defaultCustomRecurrenceOptions,
			range: defaultRecurrenceOptions.range
		};
	}

	const { pattern, range } = model;

	let rangeFormModel: RecurrenceRangeFormModel;

	switch(range.kind) {
		case AppointmentRecurrenceRangeKind.EndDate:
			rangeFormModel = {
				kind: AppointmentRecurrenceRangeKind.EndDate,
				endDate: new Date(range.endDate)
			};
			break;
		case AppointmentRecurrenceRangeKind.NoEnd:
			rangeFormModel = {
				kind: AppointmentRecurrenceRangeKind.NoEnd,
				endDate: null
			};
			break;
		case AppointmentRecurrenceRangeKind.Numbered:
			throw new Error(`AppointmentRecurrenceRangeKind ${range.kind} not supported`);
		default:
			never(range);
	}

	if (
		isCustomRecurrencePatternModel(
			pattern,
			startDate,
			timezone
		)
	) {
		return {
			recurrencePatternKind: RecurrencePatternKind.Custom,
			customRecurrenceOptions: convertPatternToCustomFormModel(
				pattern,
				startDate,
				timezone
			),
			range: rangeFormModel
		};
	}

	return {
		recurrencePatternKind: convertPatternToRecurrenceKindFormModel(pattern),
		customRecurrenceOptions: defaultCustomRecurrenceOptions,
		range: rangeFormModel
	};
}

function convertPatternToRecurrenceKindFormModel(
	pattern: RecurrencePatternModel
): RecurrencePatternKind {
	switch(pattern.kind) {
		case AppointmentRecurrencePatternKind.Daily:
			return RecurrencePatternKind.Daily;
		case AppointmentRecurrencePatternKind.Weekly:
			return RecurrencePatternKind.Weekly;
		case AppointmentRecurrencePatternKind.AbsoluteMonthly:
			return RecurrencePatternKind.AbsoluteMonthly;
		case AppointmentRecurrencePatternKind.RelativeMonthly:
			return pattern.weekIndex === AppointmentWeekIndex.Last
				? RecurrencePatternKind.RelativeMonthlyLastWeek
				: RecurrencePatternKind.RelativeMonthly;
		case AppointmentRecurrencePatternKind.AbsoluteYearly:
			return RecurrencePatternKind.Yearly;
		case AppointmentRecurrencePatternKind.RelativeYearly:
			throw new Error("There is no recurrence pattern kind for relative yearly pattern");
		default:
			never(pattern);
	}
}

function convertPatternToCustomFormModel(
	pattern: RecurrencePatternModel,
	startDate: Date,
	timezone: TimeZone
): CustomRecurrenceOptionsModel {
	const {
		repeatInterval
	} = pattern;

	const appointmentDayOfWeek: AppointmentDayOfWeek = getAppointmentDayOfWeek(
		startDate,
		timezone
	);

	const {
		monthlyCustomRecurrenceOptions,
		yearlyCustomRecurrenceOptions
	} = calendarAppointmentFormDefaultData
		.recurrenceOptions
		.customRecurrenceOptions;

	const weeklyCustomRecurrenceOptions: WeeklyCustomRecurrenceOptionsModel = {
		daysOfWeek: [appointmentDayOfWeek]
	};

	switch (pattern.kind) {
		case AppointmentRecurrencePatternKind.Daily:
			return {
				kind: CustomRecurrencePatternKind.Daily,
				weeklyCustomRecurrenceOptions,
				monthlyCustomRecurrenceOptions,
				yearlyCustomRecurrenceOptions,
				repeatInterval
			};
		case AppointmentRecurrencePatternKind.Weekly:
			return {
				kind: CustomRecurrencePatternKind.Weekly,
				weeklyCustomRecurrenceOptions: {
					daysOfWeek: pattern.daysOfWeek
				},
				monthlyCustomRecurrenceOptions,
				yearlyCustomRecurrenceOptions,
				repeatInterval
			};
		case AppointmentRecurrencePatternKind.AbsoluteMonthly:
			return {
				kind: CustomRecurrencePatternKind.Monthly,
				monthlyCustomRecurrenceOptions: {
					kind: "absolute"
				},
				weeklyCustomRecurrenceOptions,
				yearlyCustomRecurrenceOptions,
				repeatInterval
			};
		case AppointmentRecurrencePatternKind.RelativeMonthly:
			return {
				kind: CustomRecurrencePatternKind.Monthly,
				monthlyCustomRecurrenceOptions: {
					kind: pattern.weekIndex === AppointmentWeekIndex.Last
						? "relative-last-week"
						: "relative"
				},
				weeklyCustomRecurrenceOptions,
				yearlyCustomRecurrenceOptions,
				repeatInterval
			};
		case AppointmentRecurrencePatternKind.AbsoluteYearly:
			return {
				kind: CustomRecurrencePatternKind.Yearly,
				yearlyCustomRecurrenceOptions: {
					kind: "absolute"
				},
				weeklyCustomRecurrenceOptions,
				monthlyCustomRecurrenceOptions,
				repeatInterval
			};
		case AppointmentRecurrencePatternKind.RelativeYearly:
			return {
				kind: CustomRecurrencePatternKind.Yearly,
				yearlyCustomRecurrenceOptions: {
					kind: "relative"
				},
				weeklyCustomRecurrenceOptions,
				monthlyCustomRecurrenceOptions,
				repeatInterval
			};
		default:
			never(pattern);
	}
}

function isCustomRecurrencePatternModel(
	pattern: RecurrencePatternModel,
	startDate: Date,
	timezone: TimeZone
): boolean {
	if (pattern.repeatInterval > 1) {
		return true;
	}

	const appointmentRepeatSelectorOptionsValues: Exclude<
		RecurrencePatternKind,
			| RecurrencePatternKind.Custom
			| RecurrencePatternKind.DontRepeat
	>[] = getRecurrencePatternKindSelectorOptions(
		false,
		startDate,
		timezone
	)
		.map(x => x.value)
		.filter(
			x => x !== RecurrencePatternKind.Custom
				&& x !== RecurrencePatternKind.DontRepeat
		);

	const recurrencePatterns: readonly RecurrencePatternModel[] = appointmentRepeatSelectorOptionsValues
		.map(x =>
			convertRecurrencePatternKindFormModelToRecurrencePattern(
				x,
				startDate,
				timezone
			)
		);

	return recurrencePatterns.every(
		x => !areRecurrencePatternsTheSame(x, pattern)
	);
}

export function convertRecurrencePatternKindFormModelToRecurrencePattern(
	kind: Exclude<
	RecurrencePatternKind,
		| RecurrencePatternKind.Custom
		| RecurrencePatternKind.DontRepeat
	>,
	startDate: Date,
	timezone: TimeZone
): RecurrencePatternModel {
	const dayOfMonth: number = getDayOfMonth(startDate, timezone);
	const weekIndex: AppointmentWeekIndex = getWeekIndexInMonth(startDate, timezone);
	const month: number = startDate.getMonth() + 1;
	const appointmentDayOfWeek: AppointmentDayOfWeek = getAppointmentDayOfWeek(
		startDate,
		timezone
	);

	switch (kind) {
		case RecurrencePatternKind.Daily:
			return {
				kind: AppointmentRecurrencePatternKind.Daily,
				repeatInterval: 1
			};
		case RecurrencePatternKind.Weekly:
			return {
				kind: AppointmentRecurrencePatternKind.Weekly,
				daysOfWeek: [appointmentDayOfWeek],
				repeatInterval: 1
			};
		case RecurrencePatternKind.AbsoluteMonthly:
			return {
				kind: AppointmentRecurrencePatternKind.AbsoluteMonthly,
				dayOfMonth,
				repeatInterval: 1
			};
		case RecurrencePatternKind.RelativeMonthly:
			return {
				kind: AppointmentRecurrencePatternKind.RelativeMonthly,
				daysOfWeek: [appointmentDayOfWeek],
				weekIndex,
				repeatInterval: 1
			};
		case RecurrencePatternKind.RelativeMonthlyLastWeek:
			return {
				kind: AppointmentRecurrencePatternKind.RelativeMonthly,
				daysOfWeek: [appointmentDayOfWeek],
				weekIndex: AppointmentWeekIndex.Last,
				repeatInterval: 1
			};
		case RecurrencePatternKind.Yearly:
			return {
				kind:AppointmentRecurrencePatternKind.AbsoluteYearly,
				dayOfMonth,
				monthIndex: month,
				repeatInterval: 1
			};
		default:
			never(kind);
	}
}

function areRecurrencePatternsTheSame(
	a: RecurrencePatternModel,
	b: RecurrencePatternModel
): boolean {
	if (a.repeatInterval !== b.repeatInterval) {
		return false;
	}

	switch (a.kind) {
		case AppointmentRecurrencePatternKind.Daily:
			return a.kind === b.kind;
		case AppointmentRecurrencePatternKind.Weekly:
			return a.kind === b.kind
				&& areDaysOfWeekTheSame(a.daysOfWeek, b.daysOfWeek);
		case AppointmentRecurrencePatternKind.AbsoluteMonthly:
			return a.kind === b.kind
				&& a.dayOfMonth === b.dayOfMonth;
		case AppointmentRecurrencePatternKind.RelativeMonthly:
			return b.kind === a.kind
				&& a.weekIndex === b.weekIndex
				&& areDaysOfWeekTheSame(
					a.daysOfWeek,
					b.daysOfWeek
				);
		case AppointmentRecurrencePatternKind.AbsoluteYearly:
			return a.kind === b.kind
				&& a.dayOfMonth === b.dayOfMonth
				&& a.monthIndex === b.monthIndex;
		case AppointmentRecurrencePatternKind.RelativeYearly:
			return a.kind === b.kind
				&& a.weekIndex === b.weekIndex
				&& areDaysOfWeekTheSame(a.daysOfWeek, b.daysOfWeek);
		default:
			never(a);
	}
}

function areDaysOfWeekTheSame(
	a: readonly AppointmentDayOfWeek[],
	b: readonly AppointmentDayOfWeek[]
): boolean {
	if (a.length !== b.length) {
		return false;
	}

	const setA: Set<AppointmentDayOfWeek> = new Set(a);
	const setB: Set<AppointmentDayOfWeek> = new Set(b);

	return shallowComparison(setA, setB);
}
